//@ts-strict-ignore
import React, { Dispatch, ReactElement, SetStateAction, useCallback, useContext, useState } from 'react';
import createPostingPreview from '../../../views/nominal/NominalPostingPreviewForm';
import { BorderlessButton } from '../../../components/connectedForm/Button';
import { createBackgroundProcess } from '../../../utils/BackgroundJobs';
import SalesInvoiceCreditMutation from '../../../graphql/mutations/salesLedger/SalesInvoiceCreditMutation.graphql';
import SalesInvoiceCreditReInvoiceMutation from '../../../graphql/mutations/salesLedger/SalesInvoiceCreditReInvoiceMutation.graphql';
import SalesInvoiceCopyMutation from '../../../graphql/mutations/salesLedger/SalesInvoiceCopyMutation.graphql';
import SalesInvoiceTransactions from '../../../graphql/entities/salesLedger/SalesInvoiceTransactions';
import SalesInvoicePostingPreview from '../../../graphql/entities/salesLedger/SalesInvoicePostingPreview';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { WorkspaceModalOnly } from '../../../components/Workspace/WorkspaceModal';
import ModalContext from '../../../components/Context/ModalContext';
import createPostedTransactions from '../../../views/nominal/PostedTransactionsForm';
import getClientMutationId from '../../../utils/getClientMutationId';
import { useMutation } from '@apollo/client';
import SalesInvoicePostMutation from '../../../graphql/mutations/salesLedger/SalesInvoicePostMutation.graphql';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import SalesCreditFormModal from '../../../components/modals/SalesCreditFormModal';
import SalesCreditAndRefundMutation from '../../../graphql/mutations/salesLedger/SalesCreditAndRefund.graphql';
import RaiseReturnOrderFromSalesInvoiceMutation from '../../../graphql/mutations/salesLedger/RaiseReturnOrderFromSalesInvoice.graphql';
import PayByLinkModal from '../../../components/modals/PayByLinkModal';
import { ActionButton } from '../../../components/connectedForm/Button/Button';
import AppContext from '../../../components/Context/AppContext';
import {
  AlertType,
  DocumentStatus,
  IRaiseReturnOrderFromInvoiceMutationResult,
  IRaiseReturnOrderFromInvoiceMutationVariables,
  ISalesCreditAndRefundMutationResult,
  ISalesCreditAndRefundMutationVariables,
  ISalesInvoice,
  OrderOrigin,
  PaymentStatus,
} from '../../../graphql/schema/p4types';
import { IFormAction, IFormActions } from '../../../@types/entityTypes';
import { SalesCreditMiniForm } from '../../../views/salesLedger/salesCredit/salesCreditCommon';
import { useAmendInvoiceAction } from './useAmendInvoiceAction';
import { IReturnOrderModalState } from '../../../components/Context/SalesInvoiceContext';
import { useInvoicePostAction } from '../../useInvoicePostAction';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useP4ToastController } from '../../../views/system/NotificationsArea';
import { useHistory } from 'react-router';

const LazySalesInvoiceLineWorkspace = React.lazy(() => import('../../../views/salesLedger/SalesInvoiceLineWorkspace'));

export enum SalesInvoiceFormAction {
  PreviewPostings = 'SALES_LEDGER_INVOICES_RETURN_ORDER',
  ViewTransactions = 'SALES_LEDGER_INVOICES_VIEW_TRANSACTIONS',
  CreditAndReInvoice = 'SALES_LEDGER_INVOICES_CREDIT_AND_REINVOICE',
  ReturnOrder = 'SALES_LEDGER_INVOICES_RETURN_ORDER',
  ReceivePayment = 'SALES_LEDGER_INVOICES_RECEIVE_PAYMENT',
  PostInvoice = 'SALES_LEDGER_INVOICES_POST_INVOICE',
  PrintInvoice = 'SALES_LEDGER_INVOICES_PRINT_INVOICE',
  PrintCommercialInvoice = 'SALES_LEDGER_INVOICES_PRINT_COMMERCIAL_INVOICE',
  CreditRefundInvoice = 'CREDIT_REFUND_INVOICE',
  PayByLink = 'PAY_BY_LINK',
  Create = 'SALES_LEDGER_INVOICES_CREATE',
  CreateCredit = 'SALES_LEDGER_CREDITS_CREATE',
  AmendInvoice = 'SALES_LEDGER_INVOICES_AMEND_INVOICE',
}

//These are tagged in on SalesInvoiceForm.js - Still need to expose them for configuring permissions...
export const formActionsForPermissions: IFormActions<SalesInvoiceFormAction> = [
  {
    description: 'Preview Postings',
    actionReference: SalesInvoiceFormAction.PreviewPostings,
  },
  {
    description: 'View Transactions',
    actionReference: SalesInvoiceFormAction.ViewTransactions,
  },
  {
    description: 'Credit & Re-Invoice',
    actionReference: SalesInvoiceFormAction.CreditAndReInvoice,
  },
  {
    description: 'Add Credit Note',
    actionReference: SalesInvoiceFormAction.CreateCredit,
  },
  {
    actionReference: SalesInvoiceFormAction.Create,
  },
  {
    actionReference: SalesInvoiceFormAction.ReturnOrder,
  },
  {
    description: 'Receive Payment',
    actionReference: SalesInvoiceFormAction.ReceivePayment,
  },
  {
    description: 'Post Invoice',
    actionReference: SalesInvoiceFormAction.PostInvoice,
  },
  {
    description: 'Print Invoice',
    actionReference: SalesInvoiceFormAction.PrintInvoice,
  },
  {
    description: 'Print Commercial Invoice',
    actionReference: SalesInvoiceFormAction.PrintCommercialInvoice,
  },
  {
    actionReference: SalesInvoiceFormAction.CreditRefundInvoice,
  },
  {
    description: 'Pay By Link',
    actionReference: SalesInvoiceFormAction.PayByLink,
  },
  { actionReference: SalesInvoiceFormAction.AmendInvoice },
];

export const SalesInvoiceLineWorkspaceModal = ({
  modalOpen,
  setModalOpen,
  invoiceId,
  noAddOption,
}: {
  modalOpen: boolean;
  setModalOpen: Dispatch<SetStateAction<boolean>> | ((modalOpen: boolean) => void);
  invoiceId?: number;
  noAddOption?: boolean;
}) => {
  const predefinedFilters = [
    {
      filterOperation: 'EQUALS',
      filterParameters: {
        field: 'salesInvoice.id',
        value: invoiceId,
      },
    },
  ];

  return (
    <WorkspaceModalOnly isOpen={modalOpen}>
      <ModalContext.Provider value={{ onClose: () => setModalOpen(false) }}>
        <LazySalesInvoiceLineWorkspace
          viewScreenName={'/SalesLedger/Return/'}
          predefinedFilters={predefinedFilters}
          disableViewController={true}
          includeHeader={true}
          noForm={true}
          noEdit={true}
          embeddedView={true}
          onBack={() => setModalOpen(false)}
          editInNewTab={true}
          enableRowSelect={true}
          fieldNames={['returned']}
          noAddOption={noAddOption}
        />
      </ModalContext.Provider>
    </WorkspaceModalOnly>
  );
};

const useSalesInvoiceLineActions = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);

  return [
    { key: 'lineActions', text: 'Line Actions', onClick: () => setModalOpen(true) },
    <SalesInvoiceLineWorkspaceModal
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      invoiceId={connectedForm.values?.id}
    />,
  ];
};

const SALES_INVOICE_POSTING_PREVIEW = createPostingPreview(SalesInvoicePostingPreview);
const SALES_INVOICE_VIEW_TRANSACTIONS = createPostedTransactions(SalesInvoiceTransactions);

export const useCreditGenerationAction = (viaTradeCounter = false) => {
  const { dispatchSimpleToast } = useP4ToastController();
  const connectedForm = useContext(ConnectedFormContext);
  const [creditModalState, setCreditModalState] = useState<IReturnOrderModalState>({
    modalOpen: false,
  });
  const handleQueryError = useComponentQueryErrorHandling();
  const [creditAndReturnMutation] = useMutation<
    IRaiseReturnOrderFromInvoiceMutationResult,
    IRaiseReturnOrderFromInvoiceMutationVariables
  >(RaiseReturnOrderFromSalesInvoiceMutation, {
    onError: handleQueryError,
  });

  const [creditAndRefundMutation] = useMutation<
    ISalesCreditAndRefundMutationResult,
    ISalesCreditAndRefundMutationVariables
  >(SalesCreditAndRefundMutation, {
    onError: handleQueryError,
  });

  const modals = (
    <SalesCreditFormModal
      modalState={creditModalState}
      closeModal={() => {
        setCreditModalState({ modalOpen: false });
      }}
      viaTradeCounter={viaTradeCounter}
    />
  );

  const returnInvoice = (invoice: ISalesInvoice) => {
    dispatchSimpleToast('Raising Credit & Return', 'Credit note & return are being prepared.', AlertType.Info);

    creditAndReturnMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesInvoice: normalizeVariables(invoice),
        },
      },
    }).then((data) => {
      const initialValues = data?.data?.raiseReturnOrderFromInvoice?.result;
      const returnCreditUuid = initialValues?.uuid;
      if (returnCreditUuid) {
        setCreditModalState({ modalOpen: true, uuid: returnCreditUuid, initialValues: initialValues });
      }
    });
  };

  const refundInvoice = (invoice: Maybe<ISalesInvoice>) => {
    dispatchSimpleToast('Raising Credit & Refund', 'Credit note & refund are being prepared.', AlertType.Info);

    creditAndRefundMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesInvoice: normalizeVariables(invoice),
        },
      },
    }).then((data) => {
      const initialValues = data?.data?.salesCreditAndRefund?.result;
      const refundCreditUuid = initialValues?.uuid;
      if (refundCreditUuid) {
        setCreditModalState({
          modalOpen: true,
          uuid: refundCreditUuid,
          initialValues: initialValues,
          miniForm: SalesCreditMiniForm.Refund,
        });
      }
    });
  };

  const result: [
    IFormAction<SalesInvoiceFormAction>,
    ReactElement,
    { returnInvoice: Function; refundInvoice: Function },
    React.Dispatch<React.SetStateAction<IReturnOrderModalState>>
  ] = [
    {
      key: 'creditAndRefund',
      text: 'Credit & refund',
      onClick: () => refundInvoice(connectedForm?.values as Maybe<ISalesInvoice>),
      condition: isPartRefundedOrPostedAndCreditedOrPaidInvoice,
      actionReference: SalesInvoiceFormAction.CreditRefundInvoice,
      'data-testid': 'sales-invoice-action-credit-refund',
    },
    modals,
    { returnInvoice, refundInvoice },
    setCreditModalState,
  ];
  return result;
};

const PayByLinkAction = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);

  return (
    <>
      <ActionButton label="Pay By Link" onClick={() => setModalOpen(true)} />
      <ModalContext.Provider
        value={{
          onClose: () => {
            setModalOpen(false);
          },
        }}
      >
        <PayByLinkModal
          open={modalOpen}
          invoiceId={connectedForm.values?.id as number}
          paymentAmount={connectedForm.values?.headerTotal}
        />
      </ModalContext.Provider>
    </>
  );
};

export const useSalesInvoiceFormActions = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const app = useContext(AppContext);

  const [postInvoiceAction, modals] = useInvoicePostAction(SalesInvoicePostMutation);
  const [creditAndRefundAction, creditAndRefundModals] = useCreditGenerationAction();
  const [salesInvoiceLineActions, salesInvoiceLineActionsModals] = useSalesInvoiceLineActions();
  const [amendInvoiceAction, amendInvoiceModal] = useAmendInvoiceAction();

  const history = useHistory();

  const redirectCredit = useCallback(
    (salesInvoiceId: Maybe<number>) => {
      history.push({
        pathname: `/Customers/Credit/Details/New`,
        state: { previousLocation: history?.location?.pathname || 'back', salesInvoiceId },
      });
    },
    [history]
  );

  const redirectInvoice = useCallback(
    (result: any, props: any) => {
      const key = Object.keys(result)[0];
      const id = result[key].result.id;

      history.push({
        pathname: `/Customers/Invoice/Details/${id}`,
        state: {
          previousLocation: history?.location?.pathname || 'back',
          cancelButtonDisabled: !!result?.salesInvoiceCreditReInvoice,
        },
      });
    },
    [history]
  );

  const creditAndReInvoice = useCallback(
    (props: any) => {
      const { values } = props;
      const input = {
        clientMutationId: getClientMutationId(),
        id: values.id,
      };
      createBackgroundProcess(
        SalesInvoiceCreditReInvoiceMutation,
        input,
        props,
        null,
        'Credit & Re-Invoice',
        redirectInvoice
      );
    },
    [redirectInvoice]
  );

  const copyInvoice = useCallback(
    (props: any) => {
      const { values } = props;
      const input = {
        clientMutationId: getClientMutationId(),
        id: values.id,
      };
      createBackgroundProcess(SalesInvoiceCopyMutation, input, props, null, 'Duplicate Invoice', redirectInvoice);
    },
    [redirectInvoice]
  );

  const actions: [IFormActions<SalesInvoiceFormAction>, ReactElement] = [
    [
      {
        description: 'Preview Postings',
        triggerModal: {
          modalContent: SALES_INVOICE_POSTING_PREVIEW,
          entityId: 'id',
          pageView: true,
          modalProps: {
            formEntityCustomUrl: 'nominal/Postings/salesLedger/SalesInvoice',
            forceReadOnly: true,
          },
        },
        performSave: true,
        saveMessage: 'Nominal/VAT Postings Preview',
        btnClass: 'btnInfo',
        individualButton: false,
        condition: displayPreview,
        actionReference: SalesInvoiceFormAction.PreviewPostings,
        'data-testid': 'sales-invoice-action-preview-postings',
      },
      {
        description: 'View Transactions',
        triggerModal: {
          modalContent: SALES_INVOICE_VIEW_TRANSACTIONS,
          entityId: 'id',
          pageView: true,
          modalProps: {
            formEntityCustomUrl: 'nominal/Transactions/salesLedger/SalesInvoice',
            forceReadOnly: true,
          },
        },
        btnClass: 'btnInfo',
        individualButton: false,
        condition: displayTransactions,
        actionReference: SalesInvoiceFormAction.ViewTransactions,
        'data-testid': 'sales-invoice-action-view-transactions',
      },
      {
        description: 'Credit & Re-Invoice',
        fn: creditAndReInvoice,
        individualButton: false,
        condition: isPostedInvoice,
        component: BorderlessButton,
        alignLeft: true,
        permissionLabel: 'Sales Ledger Invoices Create', //Need this or we accidentally rename the permission.
        actionReference: SalesInvoiceFormAction.Create,
        'data-testid': 'sales-invoice-action-credit-re-invoice',
      },
      creditAndRefundAction,
      {
        description: 'Add Credit Note',
        fn: () => redirectCredit(connectedForm?.values?.id),
        individualButton: false,
        condition: isPostedInvoice,
        component: BorderlessButton,
        alignLeft: true,
        actionReference: SalesInvoiceFormAction.CreateCredit,
        'data-testid': 'sales-invoice-action-add-credit-note',
      },
      {
        description: 'Duplicate Invoice',
        fn: copyInvoice,
        individualButton: false,
        component: BorderlessButton,
        alignLeft: true,
        condition: canBeCopied,
        actionReference: SalesInvoiceFormAction.Create,
        'data-testid': 'sales-invoice-action-duplicate-invoice',
      },
      {
        ...salesInvoiceLineActions,
        actionReference: SalesInvoiceFormAction.CreateCredit,
        condition: () => !!connectedForm.values?.id,
        'data-testid': 'sales-invoice-action-line-actions',
      },
      {
        actionReference: SalesInvoiceFormAction.PayByLink,
        condition: () => !!(app.fullUserDetails?.flags?.motoPaymentsEnabled && connectedForm.values?.id),
        element: <PayByLinkAction />,
        'data-testid': 'sales-invoice-action-pay-by-link',
      },
      postInvoiceAction as IFormAction<SalesInvoiceFormAction>,
      amendInvoiceAction,
    ],
    <>
      {modals}
      {creditAndRefundModals}
      {salesInvoiceLineActionsModals}
      {amendInvoiceModal}
    </>,
  ];
  return actions;
};

function displayPreview(invoice: ISalesInvoice) {
  if (invoice && invoice.documentStatus && invoice.orderOrigin !== OrderOrigin.Import) {
    return invoice.documentStatus === DocumentStatus.Draft || invoice.documentStatus === DocumentStatus.Released;
  }
  return false;
}

export const isPostedInvoice = (invoice: ISalesInvoice) => {
  if (invoice && invoice.documentStatus && invoice.orderOrigin !== OrderOrigin.Import) {
    return invoice.documentStatus === DocumentStatus.Posted;
  }
  return false;
};

export const isPartRefundedOrPostedAndCreditedOrPaidInvoice = (invoice: ISalesInvoice) => {
  return !!(
    (
      invoice &&
      invoice.documentStatus &&
      invoice.orderOrigin !== OrderOrigin.Import &&
      (invoice.documentStatus === DocumentStatus.Posted || invoice.documentStatus === DocumentStatus.Credited) &&
      (invoice.paymentStatus === PaymentStatus.Paid ||
        invoice.paymentStatus === PaymentStatus.PartRefunded ||
        invoice.paymentStatus === PaymentStatus.Partpaid)
    ) //If already part refunded, can still refund other lines.
  );
};

function canBeCopied(invoice: ISalesInvoice) {
  return (
    !(invoice && invoice.creditInvoice && invoice.documentStatus === DocumentStatus.Draft) &&
    invoice.orderOrigin !== OrderOrigin.Import
  );
}

function displayTransactions(invoice: ISalesInvoice) {
  if (invoice && invoice.documentStatus && invoice.orderOrigin !== 'IMPORT') {
    return (
      invoice.documentStatus === DocumentStatus.Posted ||
      invoice.documentStatus === DocumentStatus.Credited ||
      invoice.documentStatus === DocumentStatus.Reinvoiced
    );
  }
  return false;
}
